import { isEmpty, map } from "lodash";
import { API_URL, BASIC_AUTH } from "../config/app.config";
import { ApiCode } from "../enum/auth";

const UnauthorizedPkt = {
  error: "Unauthorized",
  message: "",
  errorCode: ApiCode.UNAUTHORIZED,
};

function buildFormData<T>(formData: FormData, data: T, parentKey: string = "") {
  if (
    data &&
    typeof data === "object" &&
    !(data instanceof File) &&
    !(data instanceof Blob)
  ) {
    Object.keys(data).forEach((key) => {
      buildFormData(
        formData,
        data[key as keyof typeof data],
        parentKey ? `${parentKey}[${key}]` : key,
      );
    });
  } else if (data) {
    const value = data == null ? "" : data;

    formData.append(parentKey, value as string);
  }
}

const request = <T>(
  url: string,
  method: string,
  payload: T,
  token?: string,
  file?: Array<File>,
) => {
  let formData = undefined;
  let contentType = undefined;

  if (file) {
    formData = new FormData();

    buildFormData(formData, payload, undefined);

    if (file) {
      for (let index = 0; index < file.length; index++) {
        formData.append("files[]", file[index]);
      }
    }
  } else if (payload) {
    formData = JSON.stringify(payload);
    contentType = { "Content-Type": "application/json" };
  }

  return fetch(url, {
    method,
    body: formData,
    headers: {
      ...contentType,
      Basicauth: BASIC_AUTH,
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });
};

export const post = <T, R, S = undefined>(
  url: string,
  payload: T,
  params?: S,
  token?: string,
  file?: Array<File>,
): Promise<R> =>
  request(getAbsoluteURL<S>(url, params), "POST", payload, token, file)
    .then((res) => {
      if (res.status === 401) {
        throw new Error("Unauthorized");
      } else {
        return res.json();
      }
    })
    .catch((err) => err);

export const patch = <T, R, S = undefined>(
  url: string,
  payload: T,
  params?: S,
  token?: string,
  file?: Array<File>,
): Promise<R> =>
  request(getAbsoluteURL<S>(url, params), "PATCH", payload, token, file)
    .then((res) => {
      if (res.status === 401) {
        throw UnauthorizedPkt;
      } else {
        return res.json();
      }
    })
    .catch((err) => err);

export const get = <S, R>(
  url: string,
  params?: S,
  token?: string,
): Promise<R> =>
  request(getAbsoluteURL<S>(url, params), "GET", undefined, token)
    .then((res) => {
      if (res.status === 401) {
        throw UnauthorizedPkt;
      } else {
        return res.json();
      }
    })
    .catch((err) => err);

export const remove = <S, R>(
  url: string,
  params?: S,
  token?: string,
): Promise<R> =>
  request(getAbsoluteURL<S>(url, params), "DELETE", undefined, token)
    .then((res) => {
      if (res.status === 401) {
        throw UnauthorizedPkt;
      } else {
        return res.json();
      }
    })
    .catch((err) => err);

const getAbsoluteURL = <S>(url: string, params?: S) =>
  `${API_URL}/api${url}${toQueryString(params)}`;

const toQueryString = <S>(obj?: S) =>
  obj && !isEmpty(obj)
    ? `?${map(
        obj,
        (value, key) =>
          `${key}=${encodeURIComponent(value ? value.toString() : "")}`,
      ).join("&")}`
    : "";
